import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { DialogFooter } from '#/modules/commonComponents/dialog/DialogPrimitives';
import { getSalesOrderAllocatedQtyValueClass } from '#/modules/crmMgmt/salesOrder/helper/salesOrderAllocationHelpers';
import { formatDisplayQty } from '#/utils/formatNumber';

function candidateKey(candidate) {
  return candidate?.invStockId != null ? String(candidate.invStockId) : '';
}

function buildSelectedIdSet(initialSelectedIds = []) {
  return new Set((initialSelectedIds ?? []).map(String).filter(Boolean));
}

/**
 * 銷貨單配置：以 GET inventory-candidates 的 candidates[] 多選。
 * 可用量以 candidates.availableQty 為準（非 IMS InvStockPicker）。
 * 開啟時請用 key 重掛以重設選取。
 */
const SalesOrderInventoryCandidatePickerDialog = ({
  open,
  onOpenChange,
  candidates = [],
  initialSelectedIds = [],
  onConfirm,
  title = '請選擇欲配置的庫存',
  description = '以下為目前可配的庫存；已扣除鎖定與預留數量。',
  requiredQty = null,
  allocatedQty = null,
  isLoading = false,
  isError = false,
  errorMessage = '載入候選庫存失敗',
}) => {
  const [selectedIds, setSelectedIds] = useState(() => (
    buildSelectedIdSet(initialSelectedIds)
  ));

  const selectedCount = selectedIds.size;
  const candidateById = useMemo(() => {
    const map = new Map();
    (candidates ?? []).forEach((row) => {
      const id = candidateKey(row);
      if (id) {
        map.set(id, row);
      }
    });
    return map;
  }, [candidates]);

  function toggleId(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function handleConfirm() {
    const rows = [...selectedIds]
      .map((id) => candidateById.get(id))
      .filter(Boolean);
    onConfirm?.(rows);
    onOpenChange?.(false);
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      contentClassName={cn(dialogWideMaxWidthClassName, dialogContentCompactGapClassName)}
      footer={(
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)}>
            取消
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={isLoading || isError}>
            確認（
            {selectedCount}
            ）
          </Button>
        </DialogFooter>
      )}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <p className="text-xs text-gray-500">需求量</p>
            <p className="mt-0.5 text-base font-semibold tabular-nums">
              {requiredQty == null || requiredQty === ''
                ? '—'
                : formatDisplayQty(requiredQty)}
            </p>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <p className="text-xs text-gray-500">已配量</p>
            <p
              className={cn(
                'mt-0.5 text-base font-semibold tabular-nums',
                getSalesOrderAllocatedQtyValueClass(allocatedQty, requiredQty),
              )}
            >
              {allocatedQty == null || allocatedQty === ''
                ? '—'
                : formatDisplayQty(allocatedQty)}
            </p>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <p className="text-xs text-gray-500">已選庫存</p>
            <p className="mt-0.5 text-base font-semibold tabular-nums">
              {selectedCount}
            </p>
          </div>
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">載入候選中…</p>
        ) : null}
        {isError ? (
          <p className="text-sm text-destructive">{errorMessage}</p>
        ) : null}
        {!isLoading && !isError && (candidates?.length ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">
            此物料目前無可用庫存候選
          </p>
        ) : null}

        {!isLoading && !isError && (candidates?.length ?? 0) > 0 ? (
          <ul
            className={cn(
              'm-0 max-h-80 list-none divide-y divide-slate-100',
              'overflow-y-auto rounded border border-slate-200 p-0',
            )}
          >
            {(candidates ?? []).map((candidate) => {
              const id = candidateKey(candidate);
              if (!id) {
                return null;
              }
              const checked = selectedIds.has(id);
              const warehouseLabel = candidate?.warehouse?.name
                ?? candidate?.warehouse?.code
                ?? '—';
              return (
                <li key={id}>
                  <label
                    className={cn(
                      'flex cursor-pointer items-center gap-3 px-3 py-2.5',
                      checked && 'bg-primary-50',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="size-4 shrink-0"
                      checked={checked}
                      onChange={() => toggleId(id)}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-mono text-sm text-slate-800">
                        {candidate.stockNo || id}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {warehouseLabel}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      可用
                      {' '}
                      {formatDisplayQty(candidate.availableQty)}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </AppDialog>
  );
};

SalesOrderInventoryCandidatePickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  candidates: PropTypes.arrayOf(PropTypes.object),
  initialSelectedIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  onConfirm: PropTypes.func,
  title: PropTypes.string,
  description: PropTypes.string,
  requiredQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  allocatedQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  errorMessage: PropTypes.string,
};

export default SalesOrderInventoryCandidatePickerDialog;
